import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui';
import type { PaymentMethod, BillingDetails } from '@/types/api';
import {
  ArrowLeft,
  CreditCard,
  Building,
  Star,
  Plus,
  AlertCircle,
} from 'lucide-react';

export default function PaymentMethodsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const restaurantGroupId =
    user?.roles?.find((r) => r.restaurantGroupId != null)?.restaurantGroupId ?? null;
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAddDirectDebitModal, setShowAddDirectDebitModal] = useState(false);
  const [showEditBillingModal, setShowEditBillingModal] = useState(false);

  const { data: paymentMethods, isLoading } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => apiClient.getPaymentMethods(),
  });

  const { data: billingDetails } = useQuery({
    queryKey: ['billingDetails'],
    queryFn: () => apiClient.getBillingDetails(),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => apiClient.setDefaultPaymentMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => apiClient.removePaymentMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
  });

  const cards = paymentMethods?.filter((pm) => pm.type === 'CARD') || [];
  const directDebits = paymentMethods?.filter((pm) => pm.type === 'DIRECT_DEBIT') || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/billing" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your payment methods and billing details
          </p>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="card">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-medium text-gray-900">DEFAULT PAYMENT METHOD</h2>
        </div>
        <div className="p-4 space-y-3">
          {cards.length === 0 ? (
            <p className="text-gray-500 text-sm">No cards added yet</p>
          ) : (
            cards.map((card) => (
              <PaymentMethodCard
                key={card.id}
                method={card}
                onSetDefault={() => setDefaultMutation.mutate(card.id)}
                onRemove={() => removeMutation.mutate(card.id)}
              />
            ))
          )}
          <button
            onClick={() => setShowAddCardModal(true)}
            className="btn btn-outline h-10 w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Payment Method
          </button>
        </div>
      </div>

      {/* Direct Debit */}
      <div className="card">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-gray-900">DIRECT DEBIT (Recommended)</h2>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {directDebits.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-700">
                    Direct Debit ensures uninterrupted service. Set up via GoCardless for automatic payments.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            directDebits.map((dd) => (
              <DirectDebitCard
                key={dd.id}
                method={dd}
                onSetDefault={() => setDefaultMutation.mutate(dd.id)}
                onCancel={() => removeMutation.mutate(dd.id)}
              />
            ))
          )}
          <button
            onClick={() => setShowAddDirectDebitModal(true)}
            className="btn btn-outline h-10 w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Set Up Direct Debit
          </button>
        </div>
      </div>

      {/* Billing Details */}
      <div className="card">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-gray-900">BILLING DETAILS</h2>
            <button
              onClick={() => setShowEditBillingModal(true)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Edit Billing Details
            </button>
          </div>
        </div>
        <div className="p-4">
          {billingDetails ? (
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="text-gray-500 w-28">Company:</span>
                <span className="text-gray-900">{billingDetails.companyName}</span>
              </div>
              {billingDetails.vatNumber && (
                <div className="flex">
                  <span className="text-gray-500 w-28">VAT Number:</span>
                  <span className="text-gray-900">{billingDetails.vatNumber}</span>
                </div>
              )}
              {/* `.address` doesn't exist on the real (stub) backend response yet — guard
                  so a partially-populated response degrades instead of crashing (C1 fix). */}
              {billingDetails.address && (
                <div className="flex">
                  <span className="text-gray-500 w-28">Address:</span>
                  <span className="text-gray-900">
                    {billingDetails.address.line1}
                    {billingDetails.address.line2 ? `, ${billingDetails.address.line2}` : ''}
                    , {billingDetails.address.city}, {billingDetails.address.postcode}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No billing details set</p>
          )}
        </div>
      </div>

      {showAddCardModal && (
        <AddCardModal onClose={() => setShowAddCardModal(false)} />
      )}

      {showAddDirectDebitModal && (
        <AddDirectDebitModal
          restaurantGroupId={restaurantGroupId}
          email={user?.email}
          onClose={() => setShowAddDirectDebitModal(false)}
        />
      )}

      {showEditBillingModal && billingDetails && (
        <EditBillingDetailsModal
          details={billingDetails}
          onClose={() => setShowEditBillingModal(false)}
        />
      )}
    </div>
  );
}

interface PaymentMethodCardProps {
  method: PaymentMethod;
  onSetDefault: () => void;
  onRemove: () => void;
}

function PaymentMethodCard({ method, onSetDefault, onRemove }: PaymentMethodCardProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">
              {method.card?.brand} ending in {method.card?.last4}
              {method.isDefault && (
                <span className="ml-2 text-xs text-primary-600">
                  <Star className="h-3 w-3 inline mr-0.5" />
                  Default
                </span>
              )}
            </p>
            <p className="text-sm text-gray-500">
              Expires {method.card?.expiryMonth?.toString().padStart(2, '0')}/
              {method.card?.expiryYear}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!method.isDefault && (
            <button
              onClick={onSetDefault}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Make Default
            </button>
          )}
          <button
            onClick={onRemove}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

interface DirectDebitCardProps {
  method: PaymentMethod;
  onSetDefault: () => void;
  onCancel: () => void;
}

function DirectDebitCard({ method, onSetDefault, onCancel }: DirectDebitCardProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building className="h-6 w-6 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">
              {method.directDebit?.bankName} ••••{method.directDebit?.last4}
            </p>
            <p className="text-sm text-gray-500">
              Set up via GoCardless
              <span
                className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  method.directDebit?.mandateStatus === 'ACTIVE'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {method.directDebit?.mandateStatus}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!method.isDefault && (
            <button
              onClick={onSetDefault}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Make Default
            </button>
          )}
          <button
            onClick={onCancel}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Cancel Mandate
          </button>
        </div>
      </div>
    </div>
  );
}

function AddCardModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>Add Payment Method</ModalHeader>
      <ModalBody>
        <p className="text-gray-600 mb-4">
          This would integrate with Stripe Elements for secure card collection.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Card Number
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="4242 4242 4242 4242"
              disabled
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="MM/YY"
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CVC
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="123"
                disabled
              />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={onClose} className="btn btn-outline h-10">
          Cancel
        </button>
        <button onClick={onClose} className="btn btn-primary h-10">
          Add Card
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface AddDirectDebitModalProps {
  restaurantGroupId: number | null;
  email: string | undefined;
  onClose: () => void;
}

function AddDirectDebitModal({ restaurantGroupId, email, onClose }: AddDirectDebitModalProps) {
  // Wires to the real, working setup-mandate endpoint (C2 fix) — this previously
  // just closed the modal without ever calling GoCardless.
  const setupMandateMutation = useMutation({
    mutationFn: () => {
      if (!restaurantGroupId || !email) {
        return Promise.reject(new Error('No restaurant group or email found for this account'));
      }
      return apiClient.setupMandate({ restaurantGroupId, email });
    },
    onSuccess: (data) => {
      window.location.href = data.billingRequestFlowUrl;
    },
  });

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>Set Up Direct Debit</ModalHeader>
      <ModalBody>
        <p className="text-gray-600 mb-4">
          You'll be redirected to GoCardless to securely set up your Direct Debit mandate.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <h4 className="font-medium text-gray-900 mb-2">Benefits of Direct Debit:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Automatic payments - never miss a bill</li>
            <li>• Protected by the Direct Debit Guarantee</li>
            <li>• Lower transaction fees</li>
            <li>• Ensures uninterrupted service</li>
          </ul>
        </div>
        {setupMandateMutation.isError && (
          <p className="mt-3 text-sm text-red-600">
            {(setupMandateMutation.error as Error).message}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <button onClick={onClose} className="btn btn-outline h-10">
          Cancel
        </button>
        <button
          onClick={() => setupMandateMutation.mutate()}
          disabled={setupMandateMutation.isPending}
          className="btn btn-primary h-10"
        >
          {setupMandateMutation.isPending ? 'Redirecting…' : 'Continue to GoCardless'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface EditBillingDetailsModalProps {
  details: BillingDetails;
  onClose: () => void;
}

function EditBillingDetailsModal({ details, onClose }: EditBillingDetailsModalProps) {
  const [formData, setFormData] = useState({
    companyName: details.companyName,
    vatNumber: details.vatNumber || '',
    line1: details.address.line1,
    line2: details.address.line2 || '',
    city: details.address.city,
    postcode: details.address.postcode,
    country: details.address.country,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Would call updateBillingDetails API here
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>Edit Billing Details</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VAT Number (optional)
              </label>
              <input
                type="text"
                value={formData.vatNumber}
                onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                className="input w-full"
                placeholder="GB123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1
              </label>
              <input
                type="text"
                value={formData.line1}
                onChange={(e) => setFormData({ ...formData, line1: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2 (optional)
              </label>
              <input
                type="text"
                value={formData.line2}
                onChange={(e) => setFormData({ ...formData, line2: e.target.value })}
                className="input w-full"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode
                </label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" onClick={onClose} className="btn btn-outline h-10">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary h-10">
            Save Changes
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
